<script setup lang="ts">
import type { CmsElementProductListing } from "@shopware/composables";
import { useCmsTranslations } from "@shopware/composables";
import SwProductCard from "../SwProductCard.vue";
import ProductCardSkeleton from "../skeleton/ProductCardSkeleton.vue";
import { useCategoryListing } from "#imports";
import { computed, ref, watch } from "vue";
import { defu } from "defu";
import { useRoute, useRouter } from "vue-router";
import type { Schemas, operations } from "#shopware";
import { getProductList } from "~/server/customAPI";
import { productListTracking } from "~/components/utilities/GtmTrackings";
import { useHistoryNavigation } from "~/composables/useHistoryNavigation";

const props = defineProps<{
  content: CmsElementProductListing;
}>();

const defaultLimit = 24;
const defaultPage = 1;
const defaultOrder = "name-asc";
const isLoading = ref(false);
const categoryId = props.content.data.listing.currentFilters.navigationId;

type Translations = {
  listing: {
    noProducts: string;
    perPage: string;
    product: string;
    products: string;
  };
};
let translations: Translations = {
  listing: {
    noProducts: "No products found 😔",
    perPage: "Per Page:",
    product: "Product",
    products: "Products",
  },
};
translations = defu(useCmsTranslations(), translations) as Translations;

const {
  changeCurrentPage,
  getCurrentPage,
  getElements,
  getTotalPagesCount,
  loading,
  setInitialListing,
  getTotal,
  getCurrentFilters,
} = useCategoryListing();
const route = useRoute();
const router = useRouter();
const limit = ref(
  route.query.limit
    ? Number(route.query.limit)
    : props.content?.data?.listing?.limit
      ? Number(props.content?.data?.listing?.limit)
      : defaultLimit
);

const initalRoute = defu(route);
watch(
  () => route,
  (newRoute) => {
    if (initalRoute.path !== newRoute.path) {
      return;
    }
    if (Object.keys(newRoute.query).length > 0) {
      return;
    }
    // this fires to reset the page when query are removed/empty on client side navigation for the same page (without hard reload)
    changeCurrentPage(defaultPage, {
      limit: defaultLimit,
      p: defaultPage,
      order: defaultOrder,
    } as unknown as operations["searchPage post /search"]["body"]);
  },
  { deep: true }
);

const changePage = async (page: number) => {
  await router.push({
    query: {
      ...route.query,
      p: page,
      limit: limit.value,
    },
  });
  await changeCurrentPage(
    page,
    route.query as unknown as operations["searchPage post /search"]["body"]
  );
};

const changeLimit = async (limit: Event) => {
  const select = limit.target as HTMLSelectElement;

  await router.push({
    query: {
      ...route.query,
      limit: select.value,
      p: defaultPage,
    },
  });
  await changeCurrentPage(
    defaultPage,
    route.query as unknown as operations["searchPage post /search"]["body"]
  );
};

const isProductListing = computed(
  () => props.content?.type === "product-listing"
);
// This is a workaround because vercel caching with the nuxt preset does not support query params at the moment
// @see https://github.com/shopware/frontends/issues/687#issuecomment-1988392091
const compareRouteQueryWithInitialListing = async () => {
  const limitListing = props?.content?.data?.listing.limit ?? defaultLimit;
  const pageListing = props?.content?.data?.listing.page ?? defaultPage;
  const orderListing = props?.content?.data?.listing.sorting ?? defaultOrder;

  const isChangePageNeeded =
    (route.query.limit && limit.value !== limitListing) ||
    (route.query.p && Number(route.query.p) !== pageListing) ||
    (route.query.order && route.query.order !== orderListing)
      ? true
      : false;

  if (isChangePageNeeded) {
    const limitQuery = route.query.limit
      ? Number(route.query.limit)
      : defaultLimit;
    const pageQuery = route.query.p ? Number(route.query.p) : defaultPage;
    const orderQuery = route.query.order
      ? (route.query.order as string)
      : defaultOrder;
    const newQuery = {
      limit: limitQuery,
      p: pageQuery,
      order: orderQuery,
    };
    console.warn(
      "The current route does not match the initial listing. Changing the route to match the initial listing."
    );
    limit.value = limitQuery;
    await changeCurrentPage(
      pageQuery,
      newQuery as unknown as operations["searchPage post /search"]["body"]
    );
  }
};

setInitialListing(
  props?.content?.data?.listing as Schemas["ProductListingResult"]
);

compareRouteQueryWithInitialListing();

let productData: any = computed(() => getElements.value);

const loadMore = async (query: Object = {}) => {
  isLoading.value = true;
  const productQuery = Object.keys(query).length > 0 ? query.page : null;

  if (
    !loading.value &&
    getTotalPagesCount.value > 1 &&
    getCurrentPage.value !== getTotalPagesCount.value
  ) {
    const properties =
      getCurrentFilters.value?.properties?.length > 0
        ? getCurrentFilters.value.properties.join("|")
        : "";
    const manufacturer =
      getCurrentFilters.value?.manufacturer.length > 0
        ? getCurrentFilters.value.manufacturer.join("|")
        : "";
    const page =
      Object.keys(route.query).length > 0 && route.query.page
        ? parseInt(route.query.page) + 1
        : 2;

    const body = {
      limit: defaultLimit,
      p: productQuery ? productQuery : page,
      properties: properties,
      manufacturer: manufacturer,
      search: "",
    };

    try {
      const currentProducts = await getProductList(categoryId, body);
      if (currentProducts && Array.isArray(currentProducts.elements)) {
        productData.value.push(...currentProducts.elements); // Append new products to the existing list
        productListTracking(productData.value);
      } else {
        throw new Error(
          "Failed to fetch products or data format is incorrect."
        );
      }

      await router.push({
        query: {
          ...route.query,
          page: productQuery ? productQuery : page,
        },
      });
    } catch (error) {
      console.error("Failed to load more products:", error);
    }
  }
  isLoading.value = false;
};

const { historyState } = useHistoryNavigation();

onMounted(async () => {
  const isBack = historyState.isBackNavigation;
  const savedScrollPosition = sessionStorage.getItem("listingScrollPosition")
    ? parseInt(sessionStorage.getItem("listingScrollPosition") || "0")
    : 0;

  if (isBack && savedScrollPosition) {
    isLoading.value = true;
    window.scrollTo({
      top: 0,
      behavior: "auto",
    });

    // Add a temporary style to prevent any scrolling during the setup phase
    const styleElement = document.createElement("style");
    styleElement.id = "temp-scroll-lock";
    styleElement.textContent =
      "html, body { overflow: hidden !important; height: 100% !important; opacity: 0.75; }";
    document.head.appendChild(styleElement);
  }

  // Load initial pages if needed for any navigation type
  if (Object.keys(route.query).length > 0) {
    await loadInitalPagesFromRequest(route?.query);
  }

  // For back navigation, restore scroll position
  if (isBack && savedScrollPosition) {
    // Small delay to ensure content is loaded
    setTimeout(() => {
      // Remove scroll lock for normal navigation
      const lockStyle = document.getElementById("temp-scroll-lock");
      if (lockStyle) {
        lockStyle.remove();
      }

      window.scrollTo({
        top: savedScrollPosition,
        behavior: "auto",
      });
      isLoading.value = false;
    }, 100);
  }

  isLoading.value = false;
  productListTracking(productData.value);
});

const savedScrollPositionTrigger = () => {
  if (typeof window !== "undefined") {
    const scrollPosition = window.scrollY;
    sessionStorage.setItem("listingScrollPosition", scrollPosition.toString());
  }
};

const skeletonCount = ref(defaultLimit);

const loadInitalPagesFromRequest = async (query: Object = {}) => {
  isLoading.value = true;
  if (
    !loading.value &&
    getTotalPagesCount.value > 1 &&
    getCurrentPage.value !== getTotalPagesCount.value
  ) {
    const properties =
      getCurrentFilters.value?.properties?.length > 0
        ? getCurrentFilters.value.properties.join("|")
        : "";
    const manufacturer =
      getCurrentFilters.value?.manufacturer.length > 0
        ? getCurrentFilters.value.manufacturer.join("|")
        : "";
    const page =
      Object.keys(route.query).length > 0 && route.query.page
        ? parseInt(route.query.page) + 1
        : 2;
    skeletonCount.value = defaultLimit * (page - 1);

    try {
      for (let i = 2; i < page; i++) {
        const body = {
          limit: defaultLimit,
          p: i,
          properties: properties,
          manufacturer: manufacturer,
          search: "",
        };

        const currentProducts = await getProductList(categoryId, body);
        if (currentProducts && Array.isArray(currentProducts.elements)) {
          // Check for duplicates before adding
          const newProducts = currentProducts.elements.filter(
            (prod) =>
              !productData.value.some(
                (existingProd) => existingProd.id === prod.id
              )
          );
          productData.value.push(...newProducts);
        } else {
          throw new Error(
            "Failed to fetch products or data format is incorrect."
          );
        }
      }
    } catch (error) {
      console.error("Failed to load more products:", error);
    }
  }
  isLoading.value = false;
};
</script>

<template>
  <div class="w-full md:flex-1 shop_list md:pl-8">
    <div
      class="grid grid-cols-1 xs:grid-cols-2 laptop:grid-cols-3 gap-x-5 gap-y-10 text-center"
    >
      <SwProductCard
        v-if="!loading"
        v-for="product in getElements"
        :key="product.id"
        :product="product"
        :is-product-listing="isProductListing"
        @click="savedScrollPositionTrigger"
      />

      <ProductCardSkeleton
        v-if="loading"
        data-testid="loading"
        v-for="index in limit"
        :key="index"
      />
    </div>

    <div class="my-5">
      <div
        :class="{ hidden: productData.length == getTotal }"
        class="load-container flex justify-center mb-5"
      >
        <button
          @click="loadMore()"
          :disabled="isLoading"
          id="load-more"
          class="btn-outline uppercase"
        >
          <span v-if="isLoading">{{ $t("product.load.isLoading") }}</span>
          <span v-else>{{ $t("product.load.more") }}</span>
        </button>
      </div>

      <div class="text-center px-4">
        <p>
          {{ productData.length }} {{ $t("product.loaded.from") }}
          {{ getTotal }} {{ $t("product.loaded.shown") }}
        </p>
        <div class="mt-5 relative max-w-full w-[560px] m-auto h-1 bg-cream">
          <!-- <img src="assets/images/progress.jpg" class="m-auto block" alt=""> -->
          <div
            :class="{ hidden: productData.length == getTotal }"
            class="h-full absolute start-0 top-0 bg-black03"
            style="width: 7%"
          ></div>
        </div>
      </div>
    </div>
  </div>
</template>
<style>
html {
  scroll-behavior: smooth;
}
</style>
